import React, { useState } from 'react'
import type { ReviewPayload } from '@shared/types.js'

interface ReviewProps {
    payload: ReviewPayload
    onCancel: () => void
    onSuccess: () => void
}

export default function Review({ payload, onCancel, onSuccess }: ReviewProps) {
    const [generating, setGenerating] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(
        new Set(payload.sections.filter(s => s.includedInEpisode).map(s => s.id))
    )
    const [orderedSections, setOrderedSections] = useState(payload.sections)
    const [combineSections, setCombineSections] = useState(true)
    const [speechPaceLabel, setSpeechPaceLabel] = useState<'slow' | 'normal' | 'fast'>('normal')
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
    const [draggedId, setDraggedId] = useState<string | null>(null)
    const [showDebug, setShowDebug] = useState(false)
    const [showRaw, setShowRaw] = useState(false)

    const defaultSummarization = () => {
        if (payload.activeSummarizationLevel) return payload.activeSummarizationLevel
        if (payload.sourceType === 'dev_blog') return 'low'
        if (payload.sourceType === 'opinion') return 'none'
        if (payload.sourceType === 'news') return 'medium'
        return 'none'
    }

    const [summarizationLevel, setSummarizationLevel] = useState<import('@shared/types.js').SummarizationLevel>(defaultSummarization())
    const [summarizing, setSummarizing] = useState(false)

    const [generationMode, setGenerationMode] = useState<'standard' | 'literal'>('standard')
    const [detailMode, setDetailMode] = useState<'normal' | 'high_detail'>('normal')

    const handleGenerationModeChange = (mode: 'standard' | 'literal') => {
        setGenerationMode(mode)
        if (mode === 'literal') {
            setSummarizationLevel('none')
        }
    }

    const getTypeLabel = (type: string) => {
        switch (type) {
            case 'story': return 'Story'
            case 'quick_hits': return 'Quick Hits'
            case 'sponsor_candidate': return 'Sponsor'
            case 'promotional': return 'Promotional'
            case 'community': return 'Community'
            case 'intro_candidate': return 'Intro Candidate'
            case 'article_section': return 'Article Section'
            case 'sidebar_or_tangent': return 'Tangent'
            case 'comments': return 'Comments'
            case 'other': return 'Other'
            default: return 'Section'
        }
    }

    const handleDragStart = (e: React.DragEvent, id: string) => {
        setDraggedId(id)
        e.dataTransfer.effectAllowed = 'move'
        setTimeout(() => e.target && (e.target as HTMLElement).classList.add('opacity-50'), 0)
    }

    const handleDragEnd = (e: React.DragEvent) => {
        setDraggedId(null)
        if (e.target) (e.target as HTMLElement).classList.remove('opacity-50')
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
    }

    const handleDrop = (e: React.DragEvent, targetId: string) => {
        e.preventDefault()
        if (!draggedId || draggedId === targetId) return

        const draggedIndex = orderedSections.findIndex(s => s.id === draggedId)
        const targetIndex = orderedSections.findIndex(s => s.id === targetId)
        if (draggedIndex < 0 || targetIndex < 0) return

        const newOrder = [...orderedSections]
        const [moved] = newOrder.splice(draggedIndex, 1)
        newOrder.splice(targetIndex, 0, moved)
        setOrderedSections(newOrder)
    }

    const toggleExpand = (id: string, e: React.MouseEvent) => {
        e.stopPropagation()
        setExpandedSections(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const toggleSection = (id: string) => {
        setSelectedSectionIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const currentSelectedCount = payload.sections.filter(s => selectedSectionIds.has(s.id)).length
    const currentWordCount = payload.sections
        .filter(s => selectedSectionIds.has(s.id))
        .reduce((acc, s) => acc + (s.wordCount || 0), 0)

    const paceMultiplierMap = {
        'slow': 0.85,
        'normal': 1.0,
        'fast': 1.25
    }
    const paceMultiplier = paceMultiplierMap[speechPaceLabel]
    const currentEstimatedSeconds = Math.round((currentWordCount / (payload.baseWpm * paceMultiplier)) * 60)

    const handleRecomputeSummary = async () => {
        setSummarizing(true)
        setError(null)
        try {
            const res = await fetch(`/api/ingestions/${payload.ingestionId}/summarize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ level: summarizationLevel })
            })
            if (!res.ok) {
                const data = await res.json() as any
                throw new Error(data.error || 'Summarization failed')
            }
            const data = await res.json() as { level: string, sections: any[] }

            setOrderedSections(prev => prev.map(s => {
                const match = data.sections.find((x: any) => x.id === s.id)
                if (match) {
                    if (summarizationLevel === 'none') {
                        const orig = payload.sections.find(x => x.id === s.id)
                        return {
                            ...s,
                            content: orig?.content ?? s.content,
                            wordCount: orig?.wordCount ?? s.wordCount
                        }
                    } else if (match.content) {
                        return {
                            ...s,
                            content: match.content,
                            wordCount: match.content.trim().split(/\s+/).length
                        }
                    }
                }
                return s
            }))
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e))
        } finally {
            setSummarizing(false)
        }
    }

    const handleApprove = async () => {
        setGenerating(true)
        setError(null)
        try {
            const res = await fetch('/api/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ingestionId: payload.ingestionId,
                    profileId: payload.profileId,
                    includedSectionIds: orderedSections.filter(s => selectedSectionIds.has(s.id)).map(s => s.id),
                    combineSections,
                    speechPaceLabel,
                    speechPaceMultiplier: paceMultiplier,
                    baseWpm: payload.baseWpm,
                    summarizationLevel,
                    generationMode,
                    detailMode
                })
            })

            if (!res.ok) {
                const data = await res.json() as any
                throw new Error(data.error || 'Generate failed')
            }

            onSuccess()
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e))
            setGenerating(false)
        }
    }

    const formatSecs = (s: number) => {
        const m = Math.floor(s / 60)
        const xs = Math.round(s % 60)
        return `${m}m ${xs}s`
    }

    return (
        <div className="animate-fade-in" style={{ paddingBottom: '40px' }}>
            <div className="flex justify-between items-center mb-lg border-b pb-md" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <h2>Review Episode</h2>
                <div className="flex gap-sm">
                    <button className="btn btn-secondary" disabled={generating} onClick={onCancel}>Start Over</button>
                    <button className="btn btn-primary" disabled={generating || selectedSectionIds.size === 0} onClick={handleApprove}>
                        {generating ? 'Submitting...' : 'Approve & Generate'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="badge badge-error w-full text-center p-md mb-lg" style={{ padding: '12px', borderRadius: '8px' }}>
                    <strong>Failed: </strong> {error}
                </div>
            )}

            {payload.warnings.length > 0 && (
                <div className="card mb-lg" style={{ borderLeft: '4px solid var(--status-warning)' }}>
                    <h3 className="text-warning mb-sm" style={{ color: 'var(--status-warning)' }}>⚠️ Warnings</h3>
                    <ul style={{ paddingLeft: '20px', color: 'var(--text-secondary)' }}>
                        {payload.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                </div>
            )}

            {payload.telemetry && (
                <div className="mb-lg">
                    <button className="text-xs text-muted hover:text-white" onClick={() => setShowDebug(!showDebug)}>
                        {showDebug ? '▾ Hide Extraction Debug Details' : '▸ Show Extraction Debug Details'}
                    </button>
                    {showDebug && (
                        <div className="card mt-sm text-sm" style={{ background: 'rgba(0,0,0,0.3)' }}>
                            <div className="grid grid-cols-4 gap-sm mb-sm">
                                <div><strong>Parse Mode:</strong> {payload.telemetry.parseMode}</div>
                                <div><strong>Extracted Words:</strong> {payload.telemetry.extractedWords?.toLocaleString()}</div>
                                <div><strong>Headings:</strong> {payload.telemetry.headingCount}</div>
                                <div><strong>Links:</strong> {payload.telemetry.linkCount}</div>
                                {payload.telemetry.canonicalUrl && (
                                    <div className="col-span-4 truncate"><strong>Canonical URL:</strong> {payload.telemetry.canonicalUrl}</div>
                                )}
                            </div>
                            <button className="btn btn-secondary text-xs" onClick={() => setShowRaw(!showRaw)}>
                                {showRaw ? 'Hide Raw Text' : 'View Raw Extracted Text'}
                            </button>
                            {showRaw && (
                                <div className="mt-sm p-sm" style={{ height: '300px', overflowY: 'auto', background: '#111', color: '#aaa', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '12px', border: '1px solid #333', borderRadius: '4px' }}>
                                    {payload.rawText}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-4 gap-md mb-xl" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div className="card">
                    <div className="text-sm text-secondary">Source</div>
                    <div style={{ fontWeight: 600 }}>{payload.sourceTitle}</div>
                    <div className="flex items-center gap-sm mt-xs">
                        <span className="text-sm text-muted">{payload.sourceDate || 'Unknown date'}</span>
                        {payload.sourceUrl && (
                            <>
                                <span className="text-muted text-xs">•</span>
                                <a href={payload.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline hover:text-white transition-colors">
                                    View Original ↗
                                </a>
                            </>
                        )}
                        {payload.sourceType && (
                            <>
                                <span className="text-muted text-xs">•</span>
                                <span className="badge badge-neutral text-xs" style={{ textTransform: 'capitalize' }}>
                                    {payload.sourceType.replace('_', ' ')}
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <div className="card">
                    <div className="text-sm text-secondary">Selected Sections</div>
                    <div style={{ fontWeight: 600 }}>{currentSelectedCount} / {payload.sections.length}</div>
                    <div className="text-sm text-muted mt-xs">Will be processed into audio</div>
                </div>
                <div className="card flex flex-col justify-center">
                    <div className="text-sm text-secondary mb-xs">Generation Mode</div>
                    <select
                        className="p-sm text-sm"
                        style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid var(--glass-border)', borderRadius: '6px', outline: 'none' }}
                        value={generationMode}
                        onChange={e => handleGenerationModeChange(e.target.value as 'standard' | 'literal')}
                    >
                        <option value="standard" className="bg-gray-900">Standard (Scripted)</option>
                        <option value="literal" className="bg-gray-900">Literal Reading</option>
                    </select>
                    {generationMode === 'literal' && (
                        <div className="text-xs mt-xs" style={{ color: 'var(--status-warning)' }}>
                            Reads exactly as written. Light cleanup.
                        </div>
                    )}
                </div>
                <div className="card flex flex-col justify-center">
                    <div className="text-sm text-secondary mb-xs">Speech Pacing</div>
                    <select
                        className="p-sm text-sm"
                        style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid var(--glass-border)', borderRadius: '6px', outline: 'none' }}
                        value={speechPaceLabel}
                        onChange={e => setSpeechPaceLabel(e.target.value as 'slow' | 'normal' | 'fast')}
                    >
                        <option value="slow" className="bg-gray-900">Slow (0.85x)</option>
                        <option value="normal" className="bg-gray-900">Normal (1.0x)</option>
                        <option value="fast" className="bg-gray-900">Fast (1.25x)</option>
                    </select>
                </div>
                <div className="card flex flex-col justify-center">
                    <div className="text-sm text-secondary mb-xs">Summarization Level</div>
                    <div className="flex gap-sm">
                        <select
                            className="p-sm text-sm flex-1"
                            style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid var(--glass-border)', borderRadius: '6px', outline: 'none', opacity: generationMode === 'literal' ? 0.5 : 1 }}
                            value={summarizationLevel}
                            disabled={generationMode === 'literal' || summarizing}
                            onChange={e => setSummarizationLevel(e.target.value as import('@shared/types.js').SummarizationLevel)}
                        >
                            <option value="none" className="bg-gray-900">None (Original Length)</option>
                            <option value="low" className="bg-gray-900">Low (70-85% retention)</option>
                            <option value="medium" className="bg-gray-900">Medium (40-60% retention)</option>
                            <option value="high" className="bg-gray-900">High (20-35% retention)</option>
                        </select>
                        <button className="btn btn-secondary text-xs" disabled={summarizing || generationMode === 'literal'} onClick={handleRecomputeSummary}>
                            {summarizing ? 'Recomputing...' : 'Compute Length'}
                        </button>
                    </div>
                </div>
                <div className="card">
                    <div className="text-sm text-secondary">Estimated Duration</div>
                    <div style={{ fontWeight: 600 }}>{formatSecs(currentEstimatedSeconds)}</div>
                    <div className="text-sm text-muted mt-xs">
                        {currentWordCount.toLocaleString()} words @ {Math.round(payload.baseWpm * paceMultiplier)} WPM
                    </div>
                </div>
            </div>

            <div className="flex justify-between items-center mt-md">
                <div className="flex items-center gap-md">
                    <h3>Parsed Sections</h3>
                    <label className="flex items-center gap-sm cursor-pointer ml-lg p-sm" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                        <input type="checkbox" checked={combineSections} onChange={(e) => setCombineSections(e.target.checked)} style={{ accentColor: 'var(--brand-primary)', width: '18px', height: '18px' }} />
                        <span className="text-sm font-semibold">Combine into single audio file (saves AI quota)</span>
                    </label>
                </div>
                <button className="btn btn-secondary text-sm" onClick={() => setSelectedSectionIds(new Set())}>Clear Selection</button>
            </div>
            <p className="text-sm text-secondary mb-md">
                Select exactly which sections you want to turn into podcast audio. Drag and drop to reorder the final output.
            </p>

            <div className="flex-col gap-sm">
                {orderedSections.map((s, idx) => {
                    const isIncluded = selectedSectionIds.has(s.id)
                    const isPromo = s.type === 'sponsor_candidate' || s.type === 'promotional'

                    let bg = isIncluded ? 'var(--bg-card)' : 'rgba(255,255,255,0.02)'
                    let bdr = isIncluded ? '1px solid rgba(124, 58, 237, 0.4)' : 'var(--glass-border)'

                    if (isPromo && !isIncluded) {
                        bg = 'rgba(255, 255, 0, 0.05)'
                        bdr = '1px dashed rgba(255, 255, 0, 0.3)'
                    }

                    return (
                        <div
                            key={s.id}
                            className="card cursor-pointer hover:border-primary transition-colors duration-200"
                            style={{ background: bg, border: bdr, opacity: isIncluded ? 1 : 0.6 }}
                            onClick={() => toggleSection(s.id)}
                            draggable
                            onDragStart={(e) => handleDragStart(e, s.id)}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, s.id)}
                        >
                            <div className="flex justify-between items-center mb-sm">
                                <div className="flex items-center gap-sm">
                                    <div className="text-secondary cursor-grab active:cursor-grabbing mr-xs" title="Drag to reorder" onClick={(e) => e.stopPropagation()}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                                    </div>
                                    <input type="checkbox" checked={isIncluded} readOnly style={{ pointerEvents: 'none', width: '18px', height: '18px', accentColor: 'var(--brand-primary)' }} />
                                    <span className="badge badge-neutral">#{idx + 1}</span>
                                    <span style={{ fontWeight: 600 }}>{s.headline || '(No Headline)'}</span>
                                </div>
                                <div>
                                    {isIncluded ? (
                                        <span className="badge badge-success">Included</span>
                                    ) : (
                                        <span className="badge badge-neutral">Skipped</span>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-between items-center text-sm text-secondary mt-sm pt-sm" style={{ borderTop: '1px dashed rgba(255,255,255,0.1)' }}>
                                <div className="flex gap-md">
                                    <span><strong>Type:</strong> <span>{getTypeLabel(s.type)}</span></span>
                                    <span><strong>Words:</strong> {s.wordCount} ({formatSecs(s.estimatedSeconds || 0)})</span>
                                    <span><strong>Confidence:</strong> {((s.confidence ?? 0) * 100).toFixed(0)}%</span>
                                </div>
                                <button className="btn btn-secondary text-xs" style={{ padding: '2px 8px' }} onClick={(e) => toggleExpand(s.id, e)}>
                                    {expandedSections.has(s.id) ? '▾ Collapse' : '▸ Expand'}
                                </button>
                            </div>

                            {expandedSections.has(s.id) && (
                                <div className="mt-sm p-sm text-sm" style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 'var(--radius-sm)', whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>
                                    {s.content}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
